import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { PayMCopyDateStatusKeys } from '@teinor/erp/types/company/accounting/payMCopy/payMCopyDate'
import { PaymentRelatedTypesKeys } from '@teinor/erp/types/company/config/paymentType'
import { nanoid } from 'nanoid'
import { useCallback, useState } from 'react'
import { RenderPaymentMethodDateLogs } from '../../../../../../../../../app/erp/screens/Logistic/Accounting/CollectionAndPayments/ModalEditPaymentMethodDate/Subcomponents'
import { DisplayStatus } from '../../../../../../../../../baseComponents/Displays/DisplayStatus'
import LoaderDecider from '../../../../../../../../../baseComponents/LoaderDecider'
import NullComponent from '../../../../../../../../../baseComponents/NullComponent'
import TwinIcon from '../../../../../../../../../baseComponents/TwinIcon'
import TableWithoutVirtualListing, { TableWithoutDefaultRowRenderer } from '../../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { TableWithoutVirtualListingRowProps } from '../../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing/types'
import { DeleteRow } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import { CustomSelectWithSearchBar } from '../../../../../../../../../forms/CustomSelect'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../../forms/CustomSelect/functions'
import { InputCalendarStateFull } from '../../../../../../../../../forms/Input/InputCalendar'
import { RectangularInputPrice } from '../../../../../../../../../forms/Input/InputPrice'
import { getExactDateFromDaysFromDate } from '../../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { recalculateQuantitiesModalPayM } from '../../../../../../../../../utils/globals/order/paymentDates'
import useCollectionAndPaymentStatus from '../../../../../../../../../utils/hooks/accounting/useCollectionAndPaymentStatus'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import ModalLogsPaymentMethodDate from '../../../../../../OrderSellLayout/ModalsOrderSellLayout/ModalLogsPaymentMethodDate'
import { MarkAsPaidPaymentMethodDateIcon } from '../subcomponents'
import { DictPaymentTypesTy, OrderPaymentMethodDataSetTy, OrderPaymentMethodDataTy, PayMCopyDateModeAddExtended, UseOrderPaymentMethodModalProps } from '../types'
import ModalConfirmMarkAsPaidPayMCopyDate from './ModalConfirmMarkAsPaidPayMCopyDate'

interface ModalTablePaymentMethodDatesProps extends ModalTablePaymentMethodDatesLogicProps {
    orderPaymentMethodData: OrderPaymentMethodDataTy
    loading: boolean
    isReadOnly: boolean
    isFinished: boolean
    headerData: TwinDictionary
}

const ModalTablePaymentMethodDates: React.FC<ModalTablePaymentMethodDatesProps> = ({ orderPaymentMethodData, paymentTypesMemo, loading, isReadOnly, isFinished, setOrderPaymentMethodData, dictPaymentTypes, headerData, total, ...logic }) => {
    const {t} = useTwinTranslation()
    const { handleChangeField, deleteRow, customRowRender, onBlurRecalculate, openModalPayment, setOpenModalPayment, handleOnSubmitMarkAsPaid } = useModalTablePaymentMethodDatesLogic({ total, setOrderPaymentMethodData, paymentTypesMemo, dictPaymentTypes, ...logic })
    const lastRowIndex = orderPaymentMethodData?.paymentMethodDates && orderPaymentMethodData?.paymentMethodDates.length > 1 ? orderPaymentMethodData?.paymentMethodDates?.length - 2 : 0
    
    const { collectionAndPaymentStatus } = useCollectionAndPaymentStatus()

    let extraFinishedColumns: ColumnTableSchema<PayMCopyDateModeAddExtended> = []
    if(isFinished){
        extraFinishedColumns.push(
            {
                id: 'markAsPaid',
                dataKey: 'id',
                label: '',
                customRender: (parameterValue, allRowData) => <MarkAsPaidPaymentMethodDateIcon allRowData={allRowData} setOpenModal={setOpenModalPayment} />,
                className: 'overflow-visible',
                width: 40,
            },
        )
    }
    const columns: ColumnTableSchema<PayMCopyDateModeAddExtended> = [
        {
            id: 'date',
            dataKey: 'date',
            label: t('date', 'Fecha'),
            customRender: (parameterValue, allRowData) => <InputCalendarStateFull label={''} value={parameterValue} onChange={(value) => handleChangeField(allRowData.rowIndex, 'date', value)} hideLabel={true} minDate={new Date()} readOnly={isReadOnly} onlyValids={true} />,
            className: 'overflow-visible',
            width: 120
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            customRender: (parameterValue, allRowData) => <RectangularInputPrice className='payMCopyDate' readOnly={allRowData.rowIndex === lastRowIndex || isReadOnly} label={''} CurrencyId={headerData?.CurrencyId} onChange={(value) => handleChangeField(allRowData.rowIndex, 'quantity', value)} maxValue={headerData.total_total} value={parameterValue} onBlur={onBlurRecalculate} disabled={isReadOnly} />,
        },
        {
            id: 'PaymentTypeId',
            dataKey: 'PaymentTypeId',
            label: t('paymentTypeN', 'Tipo de pago'),
            customRender: (parameterValue, allRowData) => <CustomSelectWithSearchBar items={paymentTypesMemo} label='' className='w-full custom_select_hide_label height_28' sortItems={true} sortFunction={emptyExtraLineFirstSortFunction} value={parameterValue} onChange={(value) => handleChangeField(allRowData.rowIndex, 'PaymentTypeId', parseInt(value))} readOnly={isReadOnly} />,
            className: 'overflow-visible w-full',
        },
        {
            id: 'id',
            dataKey: 'id',
            label: t('info', 'Información'),
            customRender: (parameterValue, allRowData) => {
                const element = dictPaymentTypes?.[paymentTypesMemo[(allRowData?.PaymentTypeId || 1)]?.relatedType as PaymentRelatedTypesKeys]
                const Component = element?.component || NullComponent
                const myValue = allRowData?.new_extra_payment_data?.id || allRowData?.new_extra_payment_data?.value || allRowData?.payment_data
                return <Component {...allRowData} {...element?.extraProps} value={myValue} onChange={(value: any) => handleChangeField(allRowData.rowIndex, 'payment_data', value)} readOnly={isReadOnly} />
            },
            className: 'overflow-visible w-full',
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            customRender: (parameterValue) => <DisplayStatus text={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.name} colorText={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.colorText} icon={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.icon} />,
            className: 'overflow-visible w-full',
        },
        {
            id: 'logs',
            dataKey: 'id',
            label: '',
            customRender: (parameterValue, allRowData) => <RenderPaymentMethodDateLogs  {...{ allRowData, setOpenModal: setOpenModalPayment as any }} />,
            className: 'overflow-visible',
            width: 40,
        },
        ...extraFinishedColumns,
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => !isReadOnly ? <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData.rowIndex)} /> : <NullComponent />,
        }
    ]
    return (
        <LoaderDecider loading={loading}>
            <div className='flex-auto overflow-visible'>
                <TableWithoutVirtualListing data={orderPaymentMethodData.paymentMethodDates} columns={columns} rowRenderer={customRowRender} />
            </div>
            {openModalPayment?.type === 'markAsPaid' ? <ModalConfirmMarkAsPaidPayMCopyDate setOpened={() => setOpenModalPayment(null)} onSubmit={(res) => handleOnSubmitMarkAsPaid(res, openModalPayment?.allRowData?.PayMCopyId)} allRowData={openModalPayment?.allRowData|| {}} /> : null}
            {openModalPayment?.type === 'showLogs' ? <ModalLogsPaymentMethodDate setOpened={() => setOpenModalPayment(null)} onSubmit={()=>{}} allRowData={openModalPayment?.allRowData || {}} /> : null}
        </LoaderDecider>
    )
}


interface ModalTablePaymentMethodDatesLogicProps {
    total: number
    setOrderPaymentMethodData: OrderPaymentMethodDataSetTy
    dictPaymentTypes: DictPaymentTypesTy
    paymentTypesMemo: TwinDictionary
    getPaymentMethodCopyComplete: (PayMCopyId: number) => Promise<void>
}

const useModalTablePaymentMethodDatesLogic = ({ dictPaymentTypes, total, setOrderPaymentMethodData, paymentTypesMemo, getPaymentMethodCopyComplete }: ModalTablePaymentMethodDatesLogicProps) => {
    const [openModalPayment, setOpenModalPayment] = useState<UseOrderPaymentMethodModalProps>(null)

    const handleOnSubmitMarkAsPaid= useCallback( async(res: Response, PayMCopyId: number) => {
        if(res){
            await getPaymentMethodCopyComplete(PayMCopyId)
        }
    }, [getPaymentMethodCopyComplete])

    const handleChangeField = useCallback((pos: number, field: string, value: any) => {
        setOrderPaymentMethodData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            if (copy?.paymentMethodDates) {
                copy.paymentMethodDates[pos][field] = value
                if (field === 'PaymentTypeId') {
                    const paymentTypeData = dictPaymentTypes?.[paymentTypesMemo[value]?.relatedType as PaymentRelatedTypesKeys]
                    copy.paymentMethodDates[pos].payment_data = paymentTypeData?.extraProps?.value
                }
            }
            return copy
        })
    }, [setOrderPaymentMethodData, paymentTypesMemo, dictPaymentTypes])

    const deleteRow = useCallback((rowIndex: number) => {
        setOrderPaymentMethodData((old) => {
            if (!old) {
                return old
            }
            const copy = JSON.parse(JSON.stringify(old))
            if (copy?.paymentMethodDates.length > 2) {
                copy.paymentMethodDates.splice(rowIndex, 1)
            }
            recalculateQuantitiesModalPayM(total, copy?.paymentMethodDates)
            copy.errorTotalNotMatch = false
            return copy
        })
    }, [setOrderPaymentMethodData, total])

    const addRow = useCallback(() => {
        setOrderPaymentMethodData((old) => {
            if (!old?.paymentMethodDates?.length) {
                return old
            }
            const lastOne = (old?.paymentMethodDates?.length || 0) > 1 ? old?.paymentMethodDates?.[(old?.paymentMethodDates?.length || 0) - 2] : null
            const copy = JSON.parse(JSON.stringify(old))
            copy.paymentMethodDates?.splice(copy.paymentMethodDates.length - 1, 0, { subId: nanoid(), date: getExactDateFromDaysFromDate(30, lastOne?.date), status: 0 })
            recalculateQuantitiesModalPayM(total, copy.paymentMethodDates)
            copy.errorTotalNotMatch = false
            return copy
        })
    }, [setOrderPaymentMethodData, total])

    const customRowRender = useCallback((props: TableWithoutVirtualListingRowProps) => {
        if (props.rowData.subId === 'addButton') {
            return (
                <div className='cursor-pointer text-center bg-gray-EF py-8 w-full text-gray-51 ' onClick={addRow}>
                    <TwinIcon className='mr-10' icon={faPlus} />
                    <TwinTrans transKey='addRow'>Añadir fila</TwinTrans>
                </div>
            )
        }
        return <TableWithoutDefaultRowRenderer {...props} key={props.rowData?.subId || props.rowData?.id} className='overflow-visible' />
    }, [addRow])

    const onBlurRecalculate = useCallback(() => {
        setOrderPaymentMethodData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            recalculateQuantitiesModalPayM(total, copy.paymentMethodDates)
            copy.errorTotalNotMatch = false
            return copy
        })
    }, [setOrderPaymentMethodData, total])

    return { handleChangeField, deleteRow, customRowRender, onBlurRecalculate, openModalPayment, setOpenModalPayment, handleOnSubmitMarkAsPaid }
}


export default ModalTablePaymentMethodDates